import { FhirService } from './fhir.service';
import { MessageHandlerService } from './../utility/message-handler.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { Injectable, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { NGXLogger } from 'ngx-logger';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { User } from 'src/app/user/model/user';
import { SessionSetting } from '../model/session-setting';
import { IdleEvent } from '../model/idle-event';
import { MessengerService } from './messenger.service';
import { Router } from '@angular/router';

/**
 * Provides Application Idle functionnalities.
 */
@Injectable({
  providedIn: 'root'
})
export class IdleService implements OnDestroy
{
  /**
   * Event Emitter to handle Idle modal behaviour.
   */
  @Output() public timeoutShowModal: EventEmitter<IdleEvent> = new EventEmitter();

  /**
   * Indicates if the Idle has timed out.
   */
  public timedOut: boolean;

  /**
   * Session Setting object to control Idle configuration from back-end.
   */
  private sessionSetting: SessionSetting;

  /**
   * Authentication Service Subscription to get notified when the user is authenticating or logging out.
   */
  private authServiceSubscription: Subscription;

  /**
   * Subscription that check if Idle setting has been modified.
   */
  private settingUpdatedSubscription: Subscription;

  constructor(
    private authenticationService: AuthenticationService,
    private logger: NGXLogger,
    private settingService: SettingService,
    private messageHandlerService: MessageHandlerService,
    private idle: Idle,
    private messengerService: MessengerService,
    private fhirService: FhirService,
    private router: Router)
  {
    this.timedOut = false;
    this.getSessionSettings(false);

    this.settingUpdatedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'wsSettingsUpdated')
        {
          const responseMessage = JSON.parse(message.data.body);
          if (responseMessage.key.toUpperCase() === 'SESSIONIDLE' ||
            responseMessage.key.toUpperCase() === 'SESSIONTIMEOUT')
          {
            this.stopIdle();          // Stop current Idle
            this.emitEvent(false);    // Hide windows in case it is currently displayed
            this.getSessionSettings(true);
          }
        }
      }
    });
  }

  /**
   * Destroy handler.
   */
  public ngOnDestroy(): void
  {
    if (this.authServiceSubscription !== undefined) { this.authServiceSubscription.unsubscribe(); }
    if (this.settingUpdatedSubscription !== undefined) { this.settingUpdatedSubscription.unsubscribe(); }
  }

  /**
   * Start Idle watch.
   */
  public startIdle(): void
  {
    if (this.authenticationService.isAuthenticated &&
      (!this.fhirService.embedded || this.fhirService.fullSite) && // FHIR embedded Channels session will not terminate.
      !this.idle.isRunning() &&
      !this.router.url.includes('/modeler/'))         // Do not active idle if the current url is the Modeler.
    {
      this.idle.watch();
      this.logger.info('Idle: Watch started.');
      this.timedOut = false;
    }
  }

  /**
   * Stop Idle watch.
   */
  public stopIdle(): void
  {
    this.idle.stop();
    this.logger.info('Idle: Watch stopped.');
    this.timedOut = false;
  }

  /**
   * Get the session settings used for Idle configuration.
   */
  private getSessionSettings(reloadOnly: boolean): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (data) =>
      {
        this.sessionSetting = data;
        this.setIdleSubscriptions(reloadOnly);
        this.startIdle();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Send the event notification to the Idle modal dialog.
   *
   * @param show Indicates if we need to show or hide the Idle modal
   * @param countdown When Idle timed out, provide the Idle countdown before closing the Idle modal.
   */
  private emitEvent(show: boolean, countdown?: number): void
  {
    const idleEvent = new IdleEvent();

    idleEvent.show = show;
    idleEvent.countdown = countdown ? countdown : 0;
    idleEvent.maxTimeout = this.sessionSetting.timeout;
    idleEvent.hasTimedOut = this.timedOut;

    this.timeoutShowModal.next(idleEvent);
  }

  /**
   * Setup Idle subscriptions events and configurations.
   */
  private setIdleSubscriptions(reloadOnly: boolean): void
  {
    // Idle Initialization
    this.idle.setIdle(this.sessionSetting.idle);
    this.idle.setTimeout(this.sessionSetting.timeout);

    // If just a reloadOnly, no need to execute remaining method code.
    if (reloadOnly)
    {
      this.logger.info('Idle: Setting updated and Idle has been reset!',
        `Idle=${this.sessionSetting.idle}`,
        `Timeout=${this.sessionSetting.timeout}`);
      return;
    }

    // Sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe({
      next: () =>
      {
        this.logger.info('Idle: No longer active.');
        this.timedOut = false;
        this.emitEvent(false);
      }
    });

    this.idle.onTimeout.subscribe({
      next: () =>
      {
        this.logger.info('Idle: Timed out!');
        this.timedOut = true;
        this.emitEvent(false);

        // setTimeout is only used to let the dialog to refresh 100% prior log out.
        setTimeout(() =>
        {
          this.authenticationService.logout();
        });
      }
    });

    this.idle.onTimeoutWarning.subscribe({
      next: (countdown) =>
      {
        this.emitEvent(true, countdown);
      }
    });

    // Handle Idle on authentication changes
    this.authServiceSubscription = this.authenticationService.authenticationChange.subscribe({
      next: (user: User) =>
      {
        if (user && user.name)
        {
          this.startIdle();
        }
        else
        {
          this.stopIdle();
        }
      }
    });
  }
}
